import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class AccountBillingCancellationFlowPopupConfirmationComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('billing') declare billingService: Service.Billing;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;

	get perksLostAfterCancellation() {
		const boardsCount = this.billingService.getFeatureCurrent(BillingFeature.BOARDS);
		const sourcesCount = this.billingService.getFeatureCurrent(BillingFeature.SOURCES);
		const usersCount = this.billingService.getFeatureCurrent(BillingFeature.USERS);
		const perks = [
			{
				count: boardsCount,
				title: boardsCount === 1 ? 'Dashboard' : 'Dashboards',
				text: 'Your paid dashboards and reporting features will no longer be available.',
			},
			{
				count: sourcesCount,
				title: sourcesCount === 1 ? 'Connected data source' : 'Connected data sources',
				text: 'Your data connections will stop syncing.',
			},
			{
				count: usersCount,
				title: usersCount === 1 ? 'Team member' : 'Team members',
				text: 'Your team will lose access to paid reporting and collaboration features.',
			},
		];

		return perks.filter((perk) => perk.count > 0);
	}

	@action
	cancel(): void {
		this.submitInProgress = true;

		void this.flowCancellationService.sendFeedback();
		void this.billingService
			.cancelSubscription()
			.then(() => {
				this.flowCancellationService.openSubscriptionChangedPopup();
			})
			.finally(() => {
				this.submitInProgress = false;
			});
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name="AlertFill" @size="2xl" @class="attention-icon" />
			<h2>Here's what happens when you cancel</h2>

			<p>
				Your subscription will remain active through
				<strong>{{this.billingService.formattedActiveUntil}}</strong>. After that, you'll
				lose access to paid features, including Databox's latest AI capabilities.
			</p>
			{{#if this.perksLostAfterCancellation.length}}
				<ul class="perks">
					{{#each this.perksLostAfterCancellation as |item|}}
						<li>
							<strong class="perks__count">{{item.count}}</strong>
							<span><strong>{{item.title}}</strong>{{item.text}}</span>
						</li>
					{{/each}}
				</ul>
			{{/if}}

			<div class="controls2 controls2--center">
				<div class="buttons-row">
					<SubmitButtonComponent
						@text="Confirm cancellation"
						@theme="red"
						@size="big"
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.cancel}}
					/>
					<SubmitButtonComponent
						@text="Keep my subscription"
						@extraClass="save"
						@size="big"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
				</div>
			</div>
		</div>
	</template>
}
